<script setup>
import { ref, onMounted } from "vue"
import { toast } from 'vue3-toastify'

const activeTab = ref("Accueil")

const pages = ref({
  Accueil: {
    heroImage: null,
    titre: "",
    sousTitre: "",
    valeurs: [
      { valeur: "", description: "" },
      { valeur: "", description: "" },
      { valeur: "", description: "" },
    ],
    titreApropos: "",
    texteApropos: "",
  },
  "A Propos": {
    paragraphe1: "",
    imageBanniere: null,
  },
  Contact: {
    paragraphe1: "",
    imageBanniere: null,
    champs: [
      { label: "Nom", type: "text", obligatoire: true },
      { label: "Téléphone", type: "tel", obligatoire: false },
      { label: "Email", type: "email", obligatoire: true },
      { label: "Sujet", type: "text", obligatoire: false },
      { label: "Message", type: "textarea", obligatoire: true },
    ],
  },
})

function handleImageUpload(event, page, field) {
  const file = event.target.files[0]
  if (file) {
    pages.value[page][field] = URL.createObjectURL(file)
  }
}

function addChamp() {
  pages.value.Contact.champs.push({
    label: "Nouveau champ",
    type: "text",
    obligatoire: false,
  })
}

function removeChamp(index) {
  pages.value.Contact.champs.splice(index, 1)
}

const openDialog = ref(null)
function showDialog(id) { openDialog.value = id }
function closeDialog() { openDialog.value = null }

// ================================
// Sauvegarder le formulaire avec le header x-database
// ================================
async function saveForm() {
  try {
    const currentSite = localStorage.getItem("selectedSite") || "site1"

    const payload = {
      title: "Formulaire Contact",
      fields: pages.value.Contact.champs.map(champ => ({
        name: champ.label,
        type: champ.type,
        required: champ.obligatoire
      })),
    }

    const response = await fetch("https://datafuseweb-95a5588f8542.herokuapp.com/api/forms", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-database": currentSite
      },
      body: JSON.stringify(payload),
    })

    const data = await response.json()
    if (!response.ok) throw new Error(data.message || "Erreur lors de la sauvegarde")

    toast.success("Formulaire sauvegardé avec succès !")
  } catch (err) {
    console.error(err)
    toast.error("Erreur réseau, réessayez plus tard")
  }
}

// ================================
// Charger le formulaire existant avec le header x-database
// ================================
async function loadForm() {
  try {
    const currentSite = localStorage.getItem("selectedSite") || "site1"

    const response = await fetch("https://datafuseweb-95a5588f8542.herokuapp.com/api/forms", {
      headers: { "x-database": currentSite }
    })
    const data = await response.json()

    if (!response.ok) throw new Error(data.message || "Erreur lors de la récupération")

    // Vérifie que c'est bien un tableau
    const forms = Array.isArray(data) ? data : [data]

    const contactForm = forms.find(f => f.title === "Formulaire Contact")
    if (contactForm) {
      pages.value.Contact.champs = contactForm.fields.map(f => ({
        label: f.name,
        type: f.type,
        obligatoire: f.required
      }))
    }
  } catch (err) {
    console.error(err)
  }
}


onMounted(loadForm)
</script>




<template>
  <div class="max-w-full mx-auto p-4 sm:p-6 lg:p-8 text-[#1a1a1a]">
    <!-- Header -->
    <div class="flex justify-between items-center border-b border-gray-200 py-4 mb-6">
      <h1 class="text-lg font-normal">Personnalisations</h1>
      <button class=" bg-blue-600 px-4 py-2 text-white rounded-lg" @click="saveForm">
        Sauvegarder
      </button>
    </div>

    <!-- Onglets -->
    <div class="flex space-x-4 border-b mb-6">
      <button
        v-for="tab in Object.keys(pages)"
        :key="tab"
        @click="activeTab = tab"
        class="px-4 py-2 -mb-px border-b-2 transition-colors"
        :class="activeTab === tab 
          ? 'border-blue-500 text-blue-600 font-medium' 
          : 'border-transparent text-gray-500 hover:text-gray-700'"
      >
        {{ tab }}
      </button>
    </div>

    <!-- Accueil -->
    <div v-if="activeTab === 'Accueil'" class="space-y-6">
      <!-- Upload Hero -->
      <div>
        <label class="flex items-center font-medium mb-2">
          Image Hero
          <span
            class="ml-2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 text-sm cursor-pointer"
            @click="showDialog('hero')"
          >?</span>
        </label>
        <input type="file" accept="image/*" @change="e => handleImageUpload(e, 'Accueil', 'heroImage')" />
        <div v-if="pages.Accueil.heroImage" class="mt-2">
          <img :src="pages.Accueil.heroImage" alt="Preview" class="w-full max-w-md rounded-lg shadow" />
        </div>
      </div>

      <!-- Titre -->
      <div>
        <label class="flex items-center font-medium mb-2">
          Titre
          <span
            class="ml-2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 text-sm cursor-pointer"
            @click="showDialog('titre')"
          >?</span>
        </label>
        <input v-model="pages.Accueil.titre" type="text" class="w-full border rounded-lg px-3 py-2" />
      </div>

      <!-- Sous-titre -->
      <div>
        <label class="flex items-center font-medium mb-2">
          Sous-titre
          <span
            class="ml-2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 text-sm cursor-pointer"
            @click="showDialog('sousTitre')"
          >?</span>
        </label>
        <input v-model="pages.Accueil.sousTitre" type="text" class="w-full border rounded-lg px-3 py-2" />
      </div>

      <!-- Nos Valeurs -->
      <div>
        <h2 class="font-semibold text-lg mb-3">Nos Valeurs</h2>
        <div v-for="(valeur, index) in pages.Accueil.valeurs" :key="index" class="space-y-2 mb-4 p-3 border rounded-lg">
          <div>
            <label class="flex items-center text-sm font-medium">
              Valeur {{ index + 1 }}
              <span
                class="ml-2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 text-xs cursor-pointer"
                @click="showDialog('valeur' + index)"
              >?</span>
            </label>
            <input v-model="valeur.valeur" type="text" class="w-full border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label class="flex items-center text-sm font-medium">
              Description {{ index + 1 }}
              <span
                class="ml-2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 text-xs cursor-pointer"
                @click="showDialog('description' + index)"
              >?</span>
            </label>
            <textarea v-model="valeur.description" rows="2" class="w-full border rounded-lg px-3 py-2"></textarea>
          </div>
        </div>
      </div>

      <!-- A propos -->
      <div>
        <label class="flex items-center font-medium mb-2">
          Titre A propos
          <span
            class="ml-2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 text-sm cursor-pointer"
            @click="showDialog('titreApropos')"
          >?</span>
        </label>
        <input v-model="pages.Accueil.titreApropos" type="text" class="w-full border rounded-lg px-3 py-2" />
      </div>
      <div>
        <label class="flex items-center font-medium mb-2">
          Texte A propos
          <span
            class="ml-2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 text-sm cursor-pointer"
            @click="showDialog('texteApropos')"
          >?</span>
        </label>
        <textarea v-model="pages.Accueil.texteApropos" rows="4" class="w-full border rounded-lg px-3 py-2"></textarea>
      </div>
    </div>

    <!-- Dialogs -->
    <dialog v-if="openDialog" open class="p-0 rounded-lg shadow-xl w-[90%] max-w-lg">
      <div class="p-4">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-lg font-medium">Aide visuelle</h2>
          <button @click="closeDialog" class="text-gray-500 hover:text-black">✖</button>
        </div>
        <img
          v-if="openDialog === 'hero'"
          src="/images/help/hero.png"
          alt="Exemple Hero"
          class="w-full rounded-lg"
        />
        <img
          v-else-if="openDialog === 'titre'"
          src="/images/help/titre.png"
          alt="Exemple Titre"
          class="w-full rounded-lg"
        />
        <img
          v-else-if="openDialog === 'sousTitre'"
          src="/images/help/sous-titre.png"
          alt="Exemple Sous-titre"
          class="w-full rounded-lg"
        />
        <img
          v-else-if="openDialog === 'titreApropos'"
          src="/images/help/titre-apropos.png"
          alt="Exemple Titre A propos"
          class="w-full rounded-lg"
        />
        <img
          v-else-if="openDialog === 'texteApropos'"
          src="/images/help/texte-apropos.png"
          alt="Exemple Texte A propos"
          class="w-full rounded-lg"
        />
        <!-- etc. pour les autres -->
      </div>
    </dialog>

    <!-- CONTACT -->
    <div v-if="activeTab === 'Contact'" class="space-y-6">
      <!-- Texte intro -->
      <div>
        <label class="font-medium mb-2 block">Paragraphe d’intro</label>
        <textarea v-model="pages.Contact.paragraphe1" rows="3" class="w-full border rounded-lg px-3 py-2"></textarea>
      </div>

      <!-- Champs de formulaire -->
      <div>
        <h2 class="font-semibold text-lg mb-3">Champs du formulaire</h2>
        <div
          v-for="(champ, index) in pages.Contact.champs"
          :key="index"
          class="p-3 border rounded-lg mb-3 space-y-2"
        >
          <div class="flex gap-2">
            <input v-model="champ.label" type="text" placeholder="Label" class="flex-1 border rounded px-2 py-1" />
            <select v-model="champ.type" class="border rounded px-2 py-1">
              <option value="text">Texte</option>
              <option value="email">Email</option>
              <option value="tel">Téléphone</option>
              <option value="textarea">Zone de texte</option>
            </select>
            <label class="flex items-center gap-1 text-sm">
              <input type="checkbox" v-model="champ.obligatoire" /> Obligatoire
            </label>
            <button @click="removeChamp(index)" class="text-red-500 hover:underline">Supprimer</button>
          </div>
        </div>
        <button @click="addChamp" class="px-3 py-2 bg-blue-500 text-white rounded-lg shadow">
          + Ajouter un champ
        </button>
      </div>
    </div>
  </div>
</template>
